import { ChangeEvent, ClipboardEvent, FC } from 'react'
import { useController } from 'react-hook-form'

import { TextField, TextFieldProps } from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation/useLocalisation'

type UNLocodeFormTextFieldProps = {
  name: string
  label?: string
  required?: boolean
  disabled?: boolean
  textFieldProps?: TextFieldProps
}

const convertToUNLocode = (value: string) => {
  if (!value) {
    return ''
  }

  return value
    .toUpperCase()
    .replace(/[\s-]+/g, '')
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 5)
}

export const UNLocodeFormTextField: FC<UNLocodeFormTextFieldProps> = (props) => {
  const { name, label, required, disabled, textFieldProps } = props
  const { field, fieldState } = useController({ name })

  const { translate, isValidTranslationKey } = useLocalisation()

  const hint = !fieldState.error?.message
    ? undefined
    : isValidTranslationKey(fieldState.error.message)
      ? translate(fieldState.error.message)
      : fieldState.error.message

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = convertToUNLocode(event.target.value)
    field.onChange(value)
    textFieldProps?.onChange?.(event)
  }

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pastedText = event.clipboardData.getData('text')
    const formattedUNLocode = convertToUNLocode(pastedText)

    field.onChange(formattedUNLocode)
    textFieldProps?.onPaste?.(event)
  }

  return (
    <TextField
      {...textFieldProps}
      {...field}
      label={label}
      required={required}
      disabled={disabled}
      state={fieldState.error ? 'negative' : undefined}
      hint={hint}
      maxLength={5}
      value={field.value}
      onChange={onChange}
      onBlur={field.onBlur}
      onPaste={onPaste}
    />
  )
}
